import React, { useState, useEffect } from 'react';
import { getTechPageData } from '../services/techService';
import { getStrapiMedia } from '../lib/strapi';

const GreenArrow = () => (
  <svg className="w-6 h-6 md:w-8 md:h-8 text-[#50B17D] flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1" fill="currentColor" viewBox="0 0 24 24">
    <path d="M4 15h10v4l7-7-7-7v4H4v6z" />
  </svg>
);

const Technology = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getTechPageData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="py-40 text-center flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-[#50B17D] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Loading Technology...</p>
      </div>
    );
  }

  const heroImageUrl = getStrapiMedia(data.hero_Image);
  const wcLogoUrl = getStrapiMedia(data.widya_connect_logo);
  const wcMapUrl = getStrapiMedia(data.widya_connect_mapimage);

  // Parse connection text dynamically (e.g. "Farmers to ➡️ Knowledge")
  const parsedConnections = (data.widya_connect_body || [])
    .map(block => {
      if (!block.children) return null;
      const text = block.children.map(child => child.text || '').join('');
      if (!text.trim()) return null;

      const parts = text.split(/➡️|->|=>/);
      if (parts.length >= 2) {
        let left = parts[0].trim();
        let right = parts.slice(1).join('➡️').trim();
        
        // Remove trailing "to" (case-insensitive)
        if (left.toLowerCase().endsWith(' to')) {
          left = left.slice(0, -3).trim();
        }
        return { left, right };
      }
      return { left: text, right: '' };
    })
    .filter(item => item !== null);

  // Split detail description into paragraphs
  const detailParagraphs = data.widya_connect_detaildes
    ? data.widya_connect_detaildes.split('\n\n').filter(p => p.trim() !== '')
    : [];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[50vh] lg:min-h-[70vh] flex items-center justify-center">
        {heroImageUrl ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${heroImageUrl})` }}
            />
            <div className="absolute inset-0 bg-black/45" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gray-50" />
        )}
        <div className="container mx-auto px-6 text-center relative z-10 w-full max-w-[1200px] animate-fade-in-up">
          <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.15] max-w-4xl mx-auto font-heading ${heroImageUrl ? 'text-white' : 'text-widya-charcoal'}`}>
            {data.hero_title}
          </h1>
        </div>
      </section>

      {/* Section 1: WIDYAConnect Platform Overview */}
      <section className="py-20 md:py-28 px-6 bg-white border-b border-gray-100">
        <div className="container mx-auto max-w-[1200px]">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-widya-charcoal font-heading">
              {data.widya_connect_title || 'WIDYA Connect Platform Overview'}
            </h2>
            <div className="section-underline-center" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Circular Connection Diagram */}
            <div className="lg:col-span-7 flex justify-center">
              {wcLogoUrl ? (
                <div className="relative group p-3 bg-white rounded-3xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-500 overflow-hidden max-w-full">
                  <img
                    src={wcLogoUrl}
                    alt="WIDYA Connect Circular Ecosystem Diagram"
                    className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-[1.01]"
                  />
                </div>
              ) : (
                <div className="w-full h-[400px] bg-gray-50 rounded-3xl flex items-center justify-center text-gray-400 border border-dashed">
                  No Connection Diagram Available
                </div>
              )}
            </div>

            {/* Right Column: Connection Flows List */}
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-4">
                {parsedConnections.map((conn, idx) => (
                  <div 
                    key={idx} 
                    className="group flex items-center justify-between py-3 px-4 bg-gray-50/50 hover:bg-green-50/20 rounded-2xl border border-gray-100/80 transition-all duration-300"
                  >
                    {/* Left align subject, right align value with perfectly centered green arrow */}
                    <div className="grid grid-cols-[1fr_auto_1.2fr] gap-4 w-full items-center">
                      <span className="text-right text-gray-800 font-semibold text-base md:text-lg tracking-tight">
                        {conn.left} to
                      </span>
                      <div className="justify-self-center">
                        <GreenArrow />
                      </div>
                      <span className="text-left text-[#50B17D] font-bold text-base md:text-lg tracking-tight">
                        {conn.right}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 1 Caption: Full Width Bottom Callout */}
          {data.widya_connect_description && (
            <div className="mt-20 text-center max-w-4xl mx-auto">
              <p className="text-widya-body text-base md:text-lg lg:text-xl font-normal leading-relaxed">
                {data.widya_connect_description}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Section 2: WIDYA Ecosystem and Coordination Network */}
      <section className="py-20 md:py-28 px-6 bg-gray-50/30">
        <div className="container mx-auto max-w-[1200px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Flow Diagram */}
            <div className="lg:col-span-7 flex justify-center order-2 lg:order-1">
              {wcMapUrl ? (
                <div className="relative group p-4 bg-white rounded-3xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-500 overflow-hidden w-full">
                  <img
                    src={wcMapUrl}
                    alt="WIDYA Ecosystem Flow Diagram"
                    className="w-full h-auto object-contain max-h-[500px] transition-transform duration-500 group-hover:scale-[1.01]"
                  />
                </div>
              ) : (
                <div className="w-full h-[400px] bg-white rounded-3xl flex items-center justify-center text-gray-400 border border-dashed border-gray-200">
                  No Ecosystem Flow Diagram Available
                </div>
              )}
            </div>

            {/* Right Column: Detailed Flow Description */}
            <div className="lg:col-span-5 space-y-6 order-1 lg:order-2">
              <div className="space-y-6 lg:py-4">
                {detailParagraphs.map((para, idx) => (
                  <p 
                    key={idx} 
                    className="text-widya-body text-base md:text-lg leading-relaxed text-gray-600 font-normal"
                  >
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technology;